import { useContext, useMemo } from 'react';

import { ILabelProps, Label, TTranslator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column, Row } from 'react-table';

import { LocalizationContext, TLocalesKeys } from 'localization';
import { SettingsContext } from 'settings';

import { TCashbackRule } from './types';

export const UseGetColumns = (): Column<TCashbackRule>[] => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const getTypeLabelProps = (
    isPercent: boolean,
    gray: boolean,
    t: TTranslator<TLocalesKeys>
  ): ILabelProps => {
    if (isPercent) {
      return {
        text: t('percent'),
        iconName: 'PercentIcon',
        color: gray ? 'gray-primary' : 'violet',
      };
    }

    return {
      text: t('sum'),
      iconName: 'MoneyIcon',
      color: gray ? 'gray-primary' : 'blue',
    };
  };

  const formatDate = (date?: string): string => {
    if (!date) return '—';
    const dateObj = dayjs(date);

    if (!dateObj.isValid()) return '—';

    return dateObj.format('DD.MM.YYYY');
  };
  return useMemo(
    () => [
      {
        Header: t('state'),
        accessor: 'is_active',
        Cell: ({ row }) => {
          const end = dayjs(row?.original?.expires_at);
          const isExpired = end.isBefore(dayjs());
          const gray = isExpired || !row?.original?.is_active;
          return (
            <div>
              <Label
                text={t(
                  isExpired
                    ? 'cashback.ended'
                    : row?.original?.is_active
                    ? 'cashback.state.on'
                    : 'cashback.state.off'
                )}
                color={gray ? 'gray-primary' : 'green'}
              />
            </div>
          );
        },
        sort: true,
        sortType: (rowA, rowB) => {
          const getSortValue = (row: Row<TCashbackRule>): number => {
            if (row.original?.is_active) return 2;
            return 1;
          };

          const valueA = getSortValue(rowA);
          const valueB = getSortValue(rowB);

          return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
        },
        width: 170,
      },
      {
        Header: t('cashback.fill.sum'),
        accessor: 'amount',
        Cell: ({ value }) => (
          <span>
            {value} {currency.sign}
          </span>
        ),
        width: 200,
      },
      {
        Header: t('cashback'),
        accessor: 'value',
        Cell: ({ value, row }) => (
          <span>
            {value} {row?.original?.is_percent ? '%' : currency.sign}
          </span>
        ),
        width: 170,
      },
      {
        Header: t('type'),
        accessor: 'starts_at',
        Cell: ({ row }) => {
          const end = dayjs(row?.original?.expires_at);
          const isExpired = end.isBefore(dayjs());
          const gray = isExpired || !row?.original?.is_active;
          return (
            <Label {...getTypeLabelProps(row?.original?.is_percent, gray, t)} />
          );
        },
        width: 170,
      },
      {
        Header: t('cashback.start'),
        accessor: 'id',
        Cell: ({ row }) => {
          return <div>{formatDate(row?.original?.starts_at)}</div>;
        },
        width: 170,
      },
      {
        Header: t('cashback.end'),
        accessor: 'expires_at',
        Cell: ({ row }) => {
          return <div>{formatDate(row?.original?.expires_at)}</div>;
        },
        width: 170,
      },
    ],
    [t]
  );
};
